// ABOUTME: Placeholder of a node-info widget while its code downloads, built from the widget config alone.
// ABOUTME: Draws the same heading and field rows as the widget's own loading state, so the placeholder does not change shape.

import { Stack } from '@ds/coolshop';

import { useCustomPageBackAction } from '../hooks/useCustomPageBackAction';
import { nodeInfoFieldSkeletons } from '../utils/nodeInfoFieldSkeletons';
import { parseImageStyle } from '../utils/nodeInfoImageStyle';
import { NodeInfoFieldsSkeleton } from './NodeInfoFieldsSkeleton';
import { NodeInfoHeading } from './NodeInfoHeading';
import { WidgetFrame } from './WidgetFrame';

interface NodeInfoLoadingSkeletonProps {
    config?: Record<string, unknown>;
    nodeId?: string;
    slug: string;
}

// The back button of the placeholder only holds its place: the widget replaces it in a moment.
const HOLD_PLACE = () => {};

export function NodeInfoLoadingSkeleton({ config, nodeId, slug }: NodeInfoLoadingSkeletonProps) {
    const { visible: showBackButton, originLabel } = useCustomPageBackAction(
        slug,
        config?.hideBackButton === true,
    );
    const displayContentTypes = Array.isArray(config?.displayContentTypes)
        ? (config?.displayContentTypes as string[])
        : [];
    const backButtonLabel =
        typeof config?.backButtonLabel === 'string' ? config.backButtonLabel : undefined;
    const fileCards = config?.fileDisplay === 'card';

    const heading =
        config?.showHeading === true ? (
            <NodeInfoHeading
                title=""
                titleLoading
                onBack={showBackButton ? HOLD_PLACE : undefined}
                backLabel={originLabel}
                // As the widget: the overtext field is loaded data, so only the configured label shows.
                overtext={
                    config?.overtextContentTypeCode || originLabel ? undefined : backButtonLabel
                }
            />
        ) : null;

    // On a list page (no node) the widget is its heading alone.
    if (!nodeId) return heading;

    return (
        <Stack gap={2}>
            {heading}
            <WidgetFrame config={config}>
                <NodeInfoFieldsSkeleton
                    fields={nodeInfoFieldSkeletons({
                        displayContentTypes,
                        // The schema is not loaded yet: every type is unknown.
                        typeMap: {},
                        hasIcon: () => true,
                        showIcon: config?.showIcon !== false,
                        fileCards,
                        imageStyled: !!config?.imageStyle,
                    })}
                    showLabel={config?.showLabel !== false}
                    showSeparator={config?.showSeparator !== false}
                    dense={config?.dense === true}
                    fileCards={fileCards}
                    imageStyle={parseImageStyle(config?.imageStyle)}
                />
            </WidgetFrame>
        </Stack>
    );
}
